import React, { Suspense } from 'react';
import { Helmet } from "react-helmet";
import { Global, css, ThemeProvider } from '@emotion/react';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from "@apollo/client";

import apolloClient from './core/includes/apollo';
import useSetState from "./core/hooks/useSetState";
import AppContext from "./core/contexts/AppContext";
import UserSession, { HomeUser } from "./core/components/utils/UserSession";
import ErrorBoundary from "./core/components/errors/ErrorBoundary";
import { getSiteTitle, PUBLIC_URL } from "./config/config";
import Localisation from "./core/components/utils/Localisation";
import Header from "./core/components/Header";
import PageRoutes from "./core/components/PageRoutes";
import SocialButtons from "./core/components/SocialButtons";
import TermsAndConditions from "./core/components/TermsAndConditions";
import MinPasswordLength from "./core/components/utils/MinPasswordLength";
import theme from './theme';
import { fontImports } from "./theme/fonts";
import { DropdownOption } from "./core/components/form/DropDown";

const styles = css`
    ${fontImports}
        //restore sup and sub.
    sup, sub {
        font-size: smaller;
    }

    sup {
        vertical-align: super;
    }

    sub {
        vertical-align: sub;
    }

    ${theme.tippy.whiteTheme};

    html {
        font-size: ${theme.fonts.baseSize};
        line-height: ${theme.fonts.lineHeight};
        font-family: ${theme.fonts.primary};
        font-weight: ${theme.fonts.weights.light};
    }

    html,
    #root {
        width: 100%;
        height: 100%;
    }

    body {
        margin: 0;
    }

    * {
        box-sizing: border-box;
    }

    .body-hide-content {
        @media (max-width: 1074.92px) {
            height: 100vh;
            overflow: hidden;
        }
    }
`;

type DEFAULT_STATE = {
    hasUserBeenLoaded: boolean,
    user: HomeUser | null,
    country: {
        defaultCode: string,
        code: string,
        codeFromIP: string,
        name: string,
    },
    currentURLSegment?: string,
    currency: string,
    isLocalisationLoaded: boolean,
    hasConfirmedPlan: boolean,
    minPasswordLength: number,
    plans: StripePlan[],
    isLoadingPlans: boolean,
    countryCodes: CountryCode[],
    selectedCountry: CountryCode | null
    selectedPlan: StripePlan | null,
    currentNumberOfUsers: DropdownOption,
    currentPlanType: DropdownOption,
};

const DEFAULT_APP_STATE = {
    hasUserBeenLoaded: false,
    user: null,
    country: {
        defaultCode: '',
        code: '',
        codeFromIP: '',
        name: '',
    },
    currentURLSegment: '',
    isChangingEmail: false,
    currency: '$',
    isLocalisationLoaded: false,
    hasConfirmedPlan: false,
    minPasswordLength: 10,
    plans: [],
    isLoadingPlans: false,
    countryCodes: [],
    selectedCountry: null,
    selectedPlan: null,
    currentNumberOfUsers: {
        id: '',
        value: '',
    },
    currentPlanType: {
        id: '',
        value: '',
    },
};

function App() {
    const appState: any = useSetState<DEFAULT_STATE>(DEFAULT_APP_STATE);

    return (
        <ApolloProvider client={apolloClient}>
            <AppContext.Provider value={appState}>
                <ThemeProvider theme={theme}>
                    <ErrorBoundary>
                        <Helmet>
                            <title>{getSiteTitle('')}</title>
                        </Helmet>

                        <Suspense fallback={null}>
                            <UserSession />
                            <Localisation />
                            <MinPasswordLength />
                        </Suspense>

                        <BrowserRouter basename={PUBLIC_URL}>
                            <Global styles={styles} />
                            <Header />
                            <PageRoutes />
                            <SocialButtons />
                            <TermsAndConditions />
                        </BrowserRouter>
                    </ErrorBoundary>
                </ThemeProvider>

            </AppContext.Provider>
        </ApolloProvider>
    );
}

export default App;
